// Owner settings: the time zone briefs, quiet hours and "today" use (GET/PUT
// /api/settings/time-zone), how long a confirmed send waits for Undo (GET/PUT
// /api/settings/send-undo), whether rough time estimates show (GET/PUT /api/settings/estimates)
// and how far back Fresh start reaches by default (GET/PUT /api/settings/fresh-start).
import { queryOptions, useQuery } from "@tanstack/react-query";
import type { EstimatesSetting, SendUndoSetting, TimeZoneSetting } from "@shared/api";
import type { FreshStartSetting, FreshStartThreshold } from "@shared/recent";
import { api } from "./api";

const TIME_ZONE_URL = "/api/settings/time-zone";
const SEND_UNDO_URL = "/api/settings/send-undo";
const ESTIMATES_URL = "/api/settings/estimates";
const FRESH_START_URL = "/api/settings/fresh-start";

export const settingsKeys = {
  timeZone: ["settings", "timeZone"] as const,
  sendUndo: ["settings", "sendUndo"] as const,
  estimates: ["settings", "estimates"] as const,
  freshStart: ["settings", "freshStart"] as const,
};

export const settingsApi = {
  timeZone: (signal?: AbortSignal) => api<TimeZoneSetting>(TIME_ZONE_URL, { signal }),
  saveTimeZone: (timeZone: string) =>
    api<TimeZoneSetting>(TIME_ZONE_URL, { method: "PUT", body: { timeZone } }),
  sendUndo: (signal?: AbortSignal) => api<SendUndoSetting>(SEND_UNDO_URL, { signal }),
  saveSendUndo: (seconds: number) =>
    api<SendUndoSetting>(SEND_UNDO_URL, { method: "PUT", body: { seconds } }),
  estimates: (signal?: AbortSignal) => api<EstimatesSetting>(ESTIMATES_URL, { signal }),
  saveEstimates: (show: boolean) =>
    api<EstimatesSetting>(ESTIMATES_URL, { method: "PUT", body: { show } }),
  freshStart: (signal?: AbortSignal) => api<FreshStartSetting>(FRESH_START_URL, { signal }),
  saveFreshStart: (threshold: FreshStartThreshold) =>
    api<FreshStartSetting>(FRESH_START_URL, { method: "PUT", body: { threshold } }),
};

export const timeZoneQuery = queryOptions({
  queryKey: settingsKeys.timeZone,
  queryFn: ({ signal }) => settingsApi.timeZone(signal),
  staleTime: 5 * 60_000,
});

export function useTimeZoneSetting() {
  return useQuery(timeZoneQuery);
}

export function useSendUndoSetting() {
  return useQuery({
    queryKey: settingsKeys.sendUndo,
    queryFn: ({ signal }) => settingsApi.sendUndo(signal),
    staleTime: 5 * 60_000,
  });
}

export function useEstimatesSetting() {
  return useQuery({
    queryKey: settingsKeys.estimates,
    queryFn: ({ signal }) => settingsApi.estimates(signal),
    staleTime: 5 * 60_000,
  });
}

export function useFreshStartSetting() {
  return useQuery({
    queryKey: settingsKeys.freshStart,
    queryFn: ({ signal }) => settingsApi.freshStart(signal),
    staleTime: 5 * 60_000,
  });
}

/** Whether to show rough time estimates (U3); on while the setting loads (the default). */
export function useShowEstimates(): boolean {
  return useEstimatesSetting().data?.show ?? false;
}

/** The owner's time zone for wording ("yesterday afternoon"); the browser's until it loads. */
export function useOwnerTimeZone(): string | undefined {
  return useTimeZoneSetting().data?.timeZone;
}
